import styled from '@emotion/styled';
import { filterNullish } from '@modernhealth/filter-nullish';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { appClient } from 'src/config/apollo';

import closeIcon from 'src/public/img/icon-close-circle.svg';
import { color, font, mediaQueries, shadow } from 'src/shared/styles/variables';
import glasses from 'src/shared/svg/glasses.svg';
import arrowIcon from 'src/shared/svg/icon-arrow-back.svg';
import { useGetProgramRecommendationsQuery } from '../documents/getProgramRecommendations.generated';

export enum GetProgramRecommendationsPageTestID {
    ChangeYourPreferences = 'GetProgramRecommendationsPageTestID.ChangeYourPreferences',
    Resource = 'GetProgramRecommendationsPageTestID.Resource',
}

const Button = styled.button(() => ({
    border: 'none',
    background: 'none',
    padding: 8,
    transition: 'opacity .4s ease-in-out',
    position: 'absolute',
    top: 20,
    right: 20,
}));

const TileWrapper = styled.div({
    padding: 20,
    marginLeft: 15,
    marginBottom: 15,
    display: 'flex',
    position: 'relative',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '45%',
    border: `1px solid ${color.linkWaterLight}`,
    alignItems: 'center',
    boxShadow: shadow.regularShadow,
    cursor: 'pointer',
    '&:hover': {
        boxShadow: shadow.floatingShadow,
    },
    borderRadius: 12,
    [mediaQueries.sm]: {
        marginTop: 15,
        width: '90%',
    },
    minWidth: '400px',
});

const TileTitle = styled.h4(
    {
        textAlign: 'center',
        color: color.black,
        textTransform: 'capitalize',
    },
    font.heavy(font.size.medium),
);

interface TileDescriptionProps {
    quote?: boolean;
}

const TileDescription = styled.p<TileDescriptionProps>(
    props => ({
        textAlign: 'center',
        color: props.quote ? color.blueBayoux : color.steelGray,
        fontStyle: props.quote ? 'oblique' : 'normal',
    }),
    font.book(font.size.base),
);

interface TileProps {
    timeLength: number;
    title: string;
    img: string | null;
    type: string;
    onClick: () => void;
}

interface StyledIconProps {
    iconUrl: string;
}

const StyledIcon = styled('i')<StyledIconProps>(({ iconUrl }) => ({
    display: 'inline-block',
    backgroundImage: `url('${iconUrl}')`,
    height: '4.5rem',
    width: '4.5rem',
    backgroundSize: 'cover',
    backgroundPositionX: 'center',
    border: 'none',
}));

const ArrowIcon = styled('i')<StyledIconProps>(({ iconUrl }) => ({
    display: 'inline-block',
    backgroundImage: `url('${iconUrl}')`,
    height: '1.5rem',
    width: '1.5rem',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPositionX: 'center',
    border: 'none',
}));

const CardWrapper = styled.div({
    display: 'flex',
    flexDirection: 'row',
});

const TextWrapper = styled.div({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    flexDirection: 'column',
    marginLeft: 10,
});

const Tile = ({ img, title, timeLength, type, onClick }: TileProps) => (
    <TileWrapper onClick={onClick} data-testid={GetProgramRecommendationsPageTestID.Resource}>
        <CardWrapper>
            <StyledIcon iconUrl={img ?? ''} />
            <TextWrapper>
                {title && <TileTitle>{title}</TileTitle>}
                <TileDescription>{type + ' | ' + timeLength + ' min'}</TileDescription>
            </TextWrapper>
        </CardWrapper>
        <ArrowIcon iconUrl={arrowIcon} />
    </TileWrapper>
);
const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '100%',
    maxHeight: 1000,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    [mediaQueries.sm]: {
        marginTop: 50,
    },
});

const Title = styled.h3(
    {
        color: color.pickledBluewood,
        marginTop: 32,
        marginBottom: 0,
    },
    font.black(font.size.headerExtended),
);

const Description = styled.p({
    color: color.steelGray,
    maxWidth: 820,
    marginBottom: 30,
    textAlign: 'center',
    ...font.book(font.size.mediumExtended),
});

const LookingToWork = styled.p({
    color: color.oxfordBlue,
    ...font.medium(font.size.medium),
    maxWidth: 820,
    marginBottom: 30,
    textAlign: 'center',
});

const TilesWrapper = styled.div({
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    flexDirection: 'row',
    maxWidth: 1050,
    [mediaQueries.sm]: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
});

const ButtonsWrapper = styled.div({
    marginTop: 40,
    display: 'flex',
    flexDirection: 'row',
});

const SpanText = styled.span({
    color: color.royalBlue,
    cursor: 'pointer',
    textDecoration: 'underline',
});

export const GetProgramRecommendationsPage: React.FC<RouteComponentProps> = ({ history }) => {
    const { data } = useGetProgramRecommendationsQuery({
        client: appClient,
    });

    const recommendedResources = useMemo(
        () =>
            filterNullish(
                data?.screenBySlug?.screenSections?.[0]?.contentCollectionLinks.map(link => {
                    if (link.__typename === 'ProgramLinkNode') {
                        return { ...link.program, type: 'program' as const };
                    }
                    if (link.__typename === 'MeditationLinkNode') {
                        return { ...link.meditation, type: 'meditation' as const };
                    }
                    return null;
                }) || [],
            ),
        [data?.screenBySlug?.screenSections],
    );

    const { t } = useTranslation();

    return (
        <Wrapper>
            <Button aria-label="Close" onClick={() => history.replace('/dashboard')}>
                <img src={closeIcon} alt="" />
            </Button>
            <img src={glasses} alt="" />
            <Title>{t('onboarding:get_program_recommendations_page.title')}</Title>
            <Description>
                {t('onboarding:get_program_recommendations_page.description')}
            </Description>
            <TilesWrapper>
                {recommendedResources.map(resource => (
                    <Tile
                        title={resource.name}
                        key={resource.id}
                        img={resource.image ?? null}
                        timeLength={resource.duration ?? 0}
                        type={
                            resource.type === 'meditation'
                                ? t('shared:resource_type.meditation')
                                : t('shared:resource_type.program')
                        }
                        onClick={() => {
                            history.push(`/${resource.type}/${resource.slug}`);
                        }}
                    />
                ))}
            </TilesWrapper>
            <ButtonsWrapper>
                <LookingToWork>
                    {t(
                        'onboarding:get_program_recommendations_page.looking_to_work_with_a_care_professional',
                    )}{' '}
                    <SpanText
                        data-testid={GetProgramRecommendationsPageTestID.ChangeYourPreferences}
                        onClick={() => {
                            history.push('/pick-your-type-of-care');
                        }}
                    >
                        {t('onboarding:get_program_recommendations_page.change_your_preferences')}
                    </SpanText>
                </LookingToWork>
            </ButtonsWrapper>
        </Wrapper>
    );
};
